import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  OperatorFunction,
  Subscription,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DestinosModels } from '../../../Models/destinos';
import { createSimpleDestinos } from '../../../movilidad/pages/cotizador/cotizador.initializers';
import { CotizadorValidators } from '../../../movilidad/pages/cotizador/cotizador.validators';
import { AdvisorFormComponent } from '../../../shared/components/advisor-form/advisor-form.component';

export interface InitialFormData {
  nombreCliente: string;
  placa: string;
  confirPlaca: string;
  celular: string;
  ciudadCirc: string;
  tipoUso: string;
  codigoAsesor?: string;
  destinoSeleccionado: string;
}

@Component({
  selector: 'app-initial-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule, AdvisorFormComponent],
  templateUrl: './initial-form.component.html',
  styleUrl: './initial-form.component.scss',
})
export class InitialFormComponent implements OnInit, OnDestroy, OnChanges {
  @Input() codigoAsesor?: string = '';
  @Input() states: string[] = [];
  @Input() destinos: DestinosModels = createSimpleDestinos();
  @Input() set prefilledData(data: InitialFormData | null) {
    if (data) {
      this._prefilledData = data;
      this.preFillForm(data);
    }
  }

  @Output() formSubmit = new EventEmitter<InitialFormData>();
  @Output() formValidityChange = new EventEmitter<boolean>();
  @Output() criticalFieldsComplete = new EventEmitter<InitialFormData>();
  @Output() advisorSelected = new EventEmitter<any>();
  @Output() termsModalRequested = new EventEmitter<void>();

  isValidAdvisor = signal(false);
  private _prefilledData: InitialFormData | null = null;
  private formChangesSubscription: Subscription = new Subscription();

  celularPattern = environment.PATTERN_CELULAR;
  placasPattern = environment.PATTERN_PLACA;
  destinoSeleccionado = '';
  tipoUso = 'particular';

  formulario: FormGroup = new FormGroup({
    nombreCliente: new FormControl('', CotizadorValidators.nombre()),
    placa: new FormControl('', CotizadorValidators.placa()),
    confirPlaca: new FormControl('', CotizadorValidators.placaConfirmation('placa')),
    celular: new FormControl('', CotizadorValidators.celular()),
    ciudadCirc: new FormControl(''),
  });

  ngOnInit(): void {
    // Suscribirse a cambios del formulario
    this.formChangesSubscription = this.formulario.valueChanges.subscribe(() => {
      this.formValidityChange.emit(this.formulario.valid);

      // Verificar si los campos críticos están completos y válidos
      this.checkCriticalFields();
    });

    // Revalidar confirPlaca cuando placa cambie (para validación bidireccional)
    const placaSubscription = this.formulario.get('placa')?.valueChanges.subscribe(() => {
      const confirPlacaControl = this.formulario.get('confirPlaca');
      if (confirPlacaControl?.value) {
        confirPlacaControl.updateValueAndValidity();
      }
    });

    if (placaSubscription) {
      this.formChangesSubscription.add(placaSubscription);
    }
  }

  ngOnDestroy(): void {
    this.formChangesSubscription.unsubscribe();
  }

  /**
   * Actualiza el validador de ciudad cuando cambia la lista de estados
   */
  ngOnChanges(): void {
    if (this.states.length > 0) {
      this.formulario.get('ciudadCirc')?.setValidators(CotizadorValidators.ciudad(this.states));
      this.formulario.get('ciudadCirc')?.updateValueAndValidity();
    }
  }

  /**
   * Pre-llena el formulario con datos existentes (retoma de sesión)
   */
  private preFillForm(data: InitialFormData): void {
    this.formulario.patchValue({
      nombreCliente: data.nombreCliente || '',
      placa: data.placa || '',
      confirPlaca: data.confirPlaca || '',
      celular: data.celular || '',
      ciudadCirc: data.ciudadCirc || '',
    });

    // Marcar campos como touched
    if (data.nombreCliente) {
      this.formulario.get('nombreCliente')?.markAsTouched();
    }
    if (data.placa) {
      this.formulario.get('placa')?.markAsTouched();
      this.formulario.get('confirPlaca')?.markAsTouched();
    }
    if (data.celular) {
      this.formulario.get('celular')?.markAsTouched();
    }
    if (data.ciudadCirc) {
      this.formulario.get('ciudadCirc')?.markAsTouched();
    }
    
    // Always map destinoSeleccionado if available
    if (data.destinoSeleccionado) {
      this.destinoSeleccionado = data.destinoSeleccionado;
    }

    if (data.tipoUso) {
      this.tipoUso = data.tipoUso;
    }
  }

  /**
   * Verifica si los campos críticos están completos y emite evento
   */
  private checkCriticalFields(): void {
    const nombreControl = this.formulario.get('nombreCliente');
    const placaControl = this.formulario.get('placa');
    const celularControl = this.formulario.get('celular');

    const nombreValido = nombreControl?.valid && nombreControl?.value;
    const placaValida = placaControl?.valid && placaControl?.value;
    const celularValido = celularControl?.valid && celularControl?.value;

    if (nombreValido && placaValida && celularValido) {
      const formData = this.getFormData();
      this.criticalFieldsComplete.emit(formData);
    }
  }

  /**
   * Autocomplete search para ciudades
   */
  search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map((term) =>
        term.length < 2
          ? []
          : this.states
              .filter((v) => v.toLowerCase().indexOf(term.toLowerCase()) > -1)
              .slice(0, 10),
      ),
    );

  // ========== Métodos de Validación Unificados ==========

  /**
   * Verifica si un campo es válido (para estilos CSS)
   */
  isFieldValid(fieldName: string): boolean {
    const control = this.formulario.get(fieldName);
    return !!control && control.valid && control.touched;
  }

  /**
   * Verifica si un campo es inválido (para estilos CSS)
   */
  isFieldInvalid(fieldName: string): boolean {
    const control = this.formulario.get(fieldName);
    return !!control && control.invalid && control.touched;
  }

  /**
   * Obtiene el mensaje de error para un campo específico
   * ÚNICA FUENTE DE VERDAD para mensajes de error
   */
  getErrorMessage(fieldName: string): string {
    const control = this.formulario.get(fieldName);
    return CotizadorValidators.getErrorMessage(control);
  }

  /**
   * Verifica si se debe mostrar el mensaje de error
   */
  shouldShowError(fieldName: string): boolean {
    const control = this.formulario.get(fieldName);
    return !!control && control.invalid && control.touched && !!this.getErrorMessage(fieldName);
  }

  /**
   * Marca un campo como touched (útil para validación manual)
   */
  markFieldAsTouched(fieldName: string): void {
    this.formulario.get(fieldName)?.markAsTouched();
  }

  /**
   * Getter para validez del formulario completo
   */
  get formularioNoValido(): boolean {
    return this.formulario.invalid;
  }

  /**
   * Handler cuando ciudad pierde el foco - actualiza destinoSeleccionado
   */
  onCiudadBlur(): void {
    const ciudad = this.formulario.get('ciudadCirc')?.value;
    if (ciudad) {
      this.destinos.data.circulation_zones.forEach((selecDestino) => {
        if (selecDestino.label === ciudad) {
          this.destinoSeleccionado = selecDestino.value.toString();
        }
      });
    }
  }

  /**
   * Busca el ID del destino basado en el nombre de la ciudad
   */
  private findDestinoId(ciudadName: string | null | undefined): string {
    if (!ciudadName || !this.destinos || !this.destinos.data) return '';
    
    const destino = this.destinos.data.circulation_zones.find(
      (d) => d.label?.toLowerCase() === ciudadName.toLowerCase()
    );
    
    return destino ? destino.value.toString() : '';
  }

  /**
   * Handler para cambio de tipo de uso (radio button)
   */
  radioChangeHandler(event: any): void {
    this.tipoUso = event.target.value;
  }

  /**
   * Construye y retorna los datos del formulario
   */
  private getFormData(): InitialFormData {
    return {
      nombreCliente: this.formulario.get('nombreCliente')?.value || '',
      placa: this.formulario.get('placa')?.value?.toUpperCase() || '',
      confirPlaca: this.formulario.get('confirPlaca')?.value?.toUpperCase() || '',
      celular: this.formulario.get('celular')?.value || '',
      ciudadCirc: this.formulario.get('ciudadCirc')?.value || '',
      tipoUso: this.tipoUso,
      codigoAsesor: this.codigoAsesor,
      destinoSeleccionado: this.findDestinoId(this.formulario.get('ciudadCirc')?.value) || this.destinoSeleccionado,
    };
  }

  /**
   * Maneja el envío del formulario
   */
  onSubmit(): void {
    console.log('submit', !this.formularioNoValido);
    console.log('submit', this.isValidAdvisor());
    if (!this.formularioNoValido) {
      console.log('submit');
      
      const formData = this.getFormData();
      this.formSubmit.emit(formData);
    }
  }

  /**
   * Abre modal de términos y condiciones
   */
  openModal(): void {
    this.termsModalRequested.emit();
  }

  /**
   * Handler para el comportamiento del advisor
   */
  handleAdvisorBehavior(isValid: boolean): void {
    if (isValid) {
      this.isValidAdvisor.set(false);
    }

    if (!isValid) {
      this.isValidAdvisor.set(true);
    }
  }

  /**
   * Handler cuando se selecciona un advisor
   */
  handleAdvisorSelected(advisor: any): void {
    this.advisorSelected.emit(advisor);
  }
}
